import qs from 'qs';
import * as log from 'loglevel';
import uuid from 'uuid/v4';
import {
  FETCH_SEARCH_POSTS_RESULTS,
  FETCH_SEARCH_POSTS_RESULTS_SUCCESS,
  FETCH_SEARCH_POSTS_RESULTS_FAILURE,
  FETCH_SEARCH_COMMUNITIES_RESULTS,
  FETCH_SEARCH_COMMUNITIES_RESULTS_SUCCESS,
  FETCH_SEARCH_COMMUNITIES_RESULTS_FAILURE,
  FETCH_SEARCH_MEMBERS_RESULTS,
  FETCH_SEARCH_MEMBERS_RESULTS_SUCCESS,
  FETCH_SEARCH_MEMBERS_RESULTS_FAILURE,
  SET_COMMUNITY_FILTER,
  SET_SEARCH_SORT_BY,
  RESET_QUERY,
} from './actionTypes';
import api from 'src/utils/api';
import { trackFootfallEvent } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';

import paginationResultsPerPage from 'src/constants/paginationResultsPerPage';

import { setAlert } from 'src/typescript/components/alert/actions';


export const fetchSearchPosts = (query, currentPage, communityFilter, sortBy, usePrivateEndpointForMembers) => {
  return async (dispatch, getState) => {
    const { pages: { search: { searchQuery, searchId: currentSearchId } } } = getState();
    const searchId = searchQuery !== query ? uuid() : currentSearchId;
    dispatch({
      type: FETCH_SEARCH_POSTS_RESULTS,
      query,
      currentPage,
      communityFilter,
      sortBy,
      searchId,
    });
    if (!query) return;

    try {
      const isSortByDate = sortBy === 'newest';
      const isSortBySentiment = sortBy === 'sentiment';
      const limit = paginationResultsPerPage.search;
      const start = limit * (currentPage - 1);
      const { context: { lang, currentUser } } = getState();
      const currentUserId = currentUser && currentUser.userId;
      const isMember = !!currentUser
        && !!currentUser.followedCommunities.find(x => x.communityId === communityFilter);

      let communityFilterArray;

      if (communityFilter === 'all') {
        communityFilterArray = [];
      } else if (communityFilter === 'my') {
        communityFilterArray = currentUser === null
        ? []
        : currentUser.followedCommunities.map(x => x.communityId);
      } else {
        communityFilterArray = [parseInt(communityFilter)];
      }

      const params = {
        q: query,
      };

      if (start !== 0) {
        params.start = start;
      }

      if (communityFilter !== 'all') {
        if (usePrivateEndpointForMembers && isMember) {
          params['community-id'] = communityFilter;
        } else {
          params['community-ids'] = communityFilterArray;
        }
      }

      if (isSortByDate) {
        // default sort is by relevance
        params['sort-by'] = 'date';
      }

      if (isSortBySentiment) {
        params['sort-by'] = 'sentiment';
      }

      if (lang !== 'en') {
        params.lang = lang;
      }

      const requestParams = {
        'params': params,
        'paramsSerializer': params => qs.stringify(params, {arrayFormat: 'repeat'}),
      };

      const response = usePrivateEndpointForMembers && isMember
        ? await api.get(`private/search/${currentUserId}/posts`, requestParams)
        : await api.getPublic('search/posts', requestParams);

      const { data: { posts, communityAggregates, total } } = response;

      const footfallQuery = total === 0 ? `no-results:${query}` : query;
      if (communityFilter && communityFilter !== 'all') {
        trackFootfallEvent(footfallEventTypes.SEARCHED_COMMUNITY, { searchQuery: footfallQuery, communityPath: communityFilter, searchId, searchType: 'posts' });
      } else {
        trackFootfallEvent(footfallEventTypes.SEARCHED_SITE, { searchQuery: footfallQuery, searchType: 'posts', searchId });
      }

      dispatch({
        type: FETCH_SEARCH_POSTS_RESULTS_SUCCESS,
        totalResults: total,
        results: posts,
        communityFilterOptions: communityAggregates,
      });

    } catch (ex) {
      log.error(ex);
      dispatch({
        type: FETCH_SEARCH_POSTS_RESULTS_FAILURE,
      });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchSearchCommunities = (query, currentPage) => {
  return async (dispatch, getState) => {
    const { pages: { search: { searchQuery, searchId: currentSearchId } } } = getState();
    const searchId = searchQuery !== query ? uuid() : currentSearchId;
    dispatch({
      type: FETCH_SEARCH_COMMUNITIES_RESULTS,
      currentPage,
      query,
      searchId,
    });
    if (!query) return;
    try {
      const lang = getState().context.lang;
      const limit = paginationResultsPerPage.searchCommunities;
      const start = limit * (currentPage - 1);

      const params = {
        q: query,
      };

      if (lang !== 'en') {
        params.lang = lang;
      }

      if (start !== 0) {
        params.start = start;
      }

      const response = await api.getPublic('search/communities',
        {
          'params': params,
        },
      );
      const { data: { communities, total } } = response;

      const footfallQuery = total === 0 ? `no-results:${query}` : query;
      trackFootfallEvent(footfallEventTypes.SEARCHED_SITE, { searchQuery: footfallQuery, searchType: 'communities', searchId });

      dispatch({
        type: FETCH_SEARCH_COMMUNITIES_RESULTS_SUCCESS,
        totalResults: total,
        results: communities,
      });

    } catch (ex) {
      log.error(ex);
      dispatch({
        type: FETCH_SEARCH_COMMUNITIES_RESULTS_FAILURE,
      });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchSearchMembers = (query, currentPage, communityFilter) => {
  return async (dispatch, getState) => {
    const { pages: { search: { searchQuery, searchId: currentSearchId } } } = getState();
    const searchId = searchQuery !== query ? uuid() : currentSearchId;

    dispatch({
      type: FETCH_SEARCH_MEMBERS_RESULTS,
      query,
      currentPage,
      communityFilter,
      searchId,
    });

    if (!query) return;

    try {
      if (communityFilter && communityFilter !== 'all') {
        trackFootfallEvent(footfallEventTypes.SEARCHED_COMMUNITY, { searchQuery: query, communityPath: communityFilter, searchId, searchType: 'members' });
      }

      const limit = paginationResultsPerPage.searchMembers;
      const start = limit * (currentPage - 1);
      const { context: { lang, currentUser } } = getState();
      const currentUserId = currentUser && currentUser.userId;

      const params = {
        q: query,
        'community-id': communityFilter,
        limit,
      };

      if (start !== 0) {
        params.start = start;
      }

      if (lang !== 'en') {
        params.lang = lang;
      }

      const requestParams = {
        'params': params,
        'paramsSerializer': params => qs.stringify(params, {arrayFormat: 'repeat'}),
      };

      const response = await api.get(`private/search/${currentUserId}/members`, requestParams);
      const { data: { members, total } } = response;

      dispatch({
        type: FETCH_SEARCH_MEMBERS_RESULTS_SUCCESS,
        membersResults: members,
        totalResults: total,
      });

    } catch (ex) {
      log.error(ex);
      dispatch({
        type: FETCH_SEARCH_MEMBERS_RESULTS_FAILURE,
      });
      dispatch(setAlert(ex.message));
    }
  };
};

export const setSearchSortBy = (sortBy) => {
  return {
    type: SET_SEARCH_SORT_BY,
    sortBy,
  };
};

export const setCommunityFilter = (communityId) => {
  return {
    type: SET_COMMUNITY_FILTER,
    communityId,
  };
};

export const resetQuery = () => {
  return {
    type: RESET_QUERY,
  };
};
